import React, { useMemo } from 'react';
import {
  useSubscriptions,
  useSubscriptionsDispatch,
} from '@features/subscriptions/context/subscription-context';
import { FlexView, IconButton, Link, Text } from '@goodfynd/ui';
import { pluralize } from '@utils/string-util';

import type { SubscriptionCheckoutItemActionsProps } from './types';

import classes from './SubscriptionsCheckout.module.css';

export function SubscriptionCheckoutItemActions({
  allowMultiple,
  item,
}: SubscriptionCheckoutItemActionsProps) {
  const { activeSubscriptions } = useSubscriptions();
  const { removeCartItem, updateCartItem } = useSubscriptionsDispatch();

  const subscribedProduct = useMemo(() => {
    return activeSubscriptions
      .find((subscription) =>
        subscription.items.find(
          (subscriptionItem) => subscriptionItem.price.sku === item.sku
        )
      )
      ?.items.find(
        (subscriptionItem) => subscriptionItem.price.sku === item.sku
      );
  }, [activeSubscriptions, item.sku]);

  if (!allowMultiple) {
    const isCanceled =
      item.previousQuantity && item.previousQuantity > item.newQuantity;
    return (
      <Link
        as="supportingText"
        className="link-underline"
        color="TextIconSecondary"
        href=""
        value={isCanceled ? 'Uncancel' : 'Remove'}
        onClick={() => {
          if (isCanceled && item.previousQuantity) {
            updateCartItem({ ...item, quantity: item.previousQuantity });
            return;
          }

          removeCartItem(item.sku);
        }}
      />
    );
  }

  return (
    <>
      <FlexView align="center" justify="space-around" width={112}>
        <IconButton
          className={classes['quantity-button']}
          disabled={!item.quantity}
          display="compact"
          icon="remove"
          size="small"
          onClick={() => {
            const newQuantity = item.quantity - 1;
            if (!newQuantity && !subscribedProduct?.quantity) {
              removeCartItem(item.sku);
              return;
            }

            updateCartItem({ ...item, quantity: newQuantity });
          }}
        />

        <Text as="subhead2">{item.quantity}</Text>

        <IconButton
          className={classes['quantity-button']}
          display="compact"
          icon="add"
          size="small"
          onClick={() => {
            updateCartItem({ ...item, quantity: item.quantity + 1 });
          }}
        />
      </FlexView>

      {subscribedProduct && item.previousQuantity && (
        <FlexView justify="center" width="100%">
          <Text as="body2">
            {item.previousQuantity} current&nbsp;
            {pluralize('seat', item.previousQuantity)}
          </Text>
        </FlexView>
      )}
    </>
  );
}
